import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PaymentOption, PaymentOptionsError, PaymentOptionsStatus, TerminalErrorCode } from '../../model'
import { PaymentType } from '@afterpay/types'
import { PURGE } from 'redux-persist'
import { createDocuments, getPaymentOptions, updateCheckout, updatePaymentOption, updatePaymentOptions } from '../checkout/actions'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ErrorCode } from '@afterpaytouch/portal-api'

export interface CreditFreezeRetryContext {
  // Application deadline copied from the prior CreditFreezeDeclinedPCL error so a follow-up
  // 429 during retry can rebuild the error with the original deadline (the 429 response itself
  // does not include it).
  deadline: string
  errorCode: ErrorCode
}

export interface ConsumerLendingState {
  selectedPaymentType?: PaymentType
  selectedPaymentOptionId?: number
  isAutopayEnabled?: boolean
  paymentOptions: PaymentOption[]
  paymentOptionsStatus: PaymentOptionsStatus
  loanAgreementDocumentId?: string
  paymentAuthorisationDocumentId?: string
  creditScoreDisclosureDocumentId?: string
  showEfaModal: boolean
  error?: PaymentOptionsError
  // Set by `resetPaymentOptionsForRetry` after a credit-freeze decline so the 429 handler can
  // still route back to RETRY_CREDIT_FREEZE even though `state.error` has been cleared. Cleared
  // by `setPaymentOptionsError`, `clearPaymentOptionsError`, and `getPaymentOptions.fulfilled`.
  creditFreezeRetryContext?: CreditFreezeRetryContext
}

export const consumerLendingInitialState: ConsumerLendingState = {
  selectedPaymentType: undefined,
  selectedPaymentOptionId: undefined,
  isAutopayEnabled: true,
  paymentOptions: [],
  paymentOptionsStatus: PaymentOptionsStatus.Initial,
  loanAgreementDocumentId: undefined,
  paymentAuthorisationDocumentId: undefined,
  showEfaModal: false,
  error: undefined,
  creditFreezeRetryContext: undefined,
}

const slice = createSlice({
  name: 'consumerLending',
  initialState: consumerLendingInitialState,
  reducers: {
    setSelectedPaymentType(state: ConsumerLendingState, action: PayloadAction<PaymentType>) {
      state.selectedPaymentType = action.payload
    },
    setSelectedPaymentOptionId(state: ConsumerLendingState, action: PayloadAction<number>) {
      state.selectedPaymentOptionId = action.payload
    },
    setIsUpdatingCLAutopayEnabled(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.isAutopayEnabled = action.payload
    },
    setPaymentOptionsError(state: ConsumerLendingState, action: PayloadAction<PaymentOptionsError>) {
      state.error = action.payload
      // its assumed that if a payment options error happens, the customer will be forced to use PBI.
      state.selectedPaymentType = PaymentType.PBI
      // A fresh error supersedes any stashed retry context — either the 429 handler just
      // rebuilt the credit-freeze error (context is now stale) or a new error has replaced the
      // prior one entirely.
      state.creditFreezeRetryContext = undefined
    },
    setShowEfaModal(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.showEfaModal = action.payload
    },
    clearPaymentOptionsError(state: ConsumerLendingState) {
      state.error = undefined
      state.creditFreezeRetryContext = undefined
    },
    // Resets fetch state so a user-initiated retry (e.g. removing a credit freeze and clicking
    // "Retry with Pay Monthly") can re-fetch options. The inline expandable variant guards its
    // fetch effect on `paymentOptionsStatus === Initial` to prevent 429 retry loops, so a prior
    // `Error` status would otherwise block the retry. Also clears the PBI lock-in applied by
    // `setPaymentOptionsError` so the user can reselect Pay Monthly. `state.error` is cleared so
    // the PCL radio (disabled while any error exists) is re-enabled and the route-to-summary
    // effect (gated on `isCreditFreezeDeclineFlow`) no longer blocks a PBI fallback; the prior
    // credit-freeze deadline is stashed into `creditFreezeRetryContext` so the 429 handler can
    // still route a rate-limited retry back to RETRY_CREDIT_FREEZE with the right deadline.
    resetPaymentOptionsForRetry(state: ConsumerLendingState) {
      const priorError = state.error
      state.paymentOptionsStatus = PaymentOptionsStatus.Initial
      state.error = undefined
      state.selectedPaymentType = undefined
      const deadline = priorError?.extra?.creditFreezeApplicationDeadline
      if (priorError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL && deadline != null && deadline !== '' && priorError.errorCode != null) {
        state.creditFreezeRetryContext = { deadline, errorCode: priorError.errorCode }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
        state.creditFreezeRetryContext = undefined
      })
      .addCase(getPaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(getPaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(updatePaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOption.fulfilled, (state, action) => {
        state.selectedPaymentType = action.payload.paymentOption.paymentType
        state.selectedPaymentOptionId = action.payload.paymentOption.id
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOption.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(updatePaymentOption.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(createDocuments.fulfilled, (state, action) => {
        const response = action.payload
        state.loanAgreementDocumentId = response.LOAN_AGREEMENT
        state.paymentAuthorisationDocumentId = response.PAYMENT_AUTHORISATION
        state.creditScoreDisclosureDocumentId = response.CREDIT_SCORE_DISCLOSURE
      })
      .addCase(createDocuments.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(createDocuments.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(updateCheckout.fulfilled, (state, action) => {
        const { paymentOptions } = action.payload
        if (valueIsNotNullOrUndefined(paymentOptions)) {
          state.paymentOptions = paymentOptions
          // TODO: Add selected payment options here once payment option is removed.
        }
      })
      .addCase(PURGE, () => consumerLendingInitialState)
  },
})

export const {
  setSelectedPaymentType,
  setSelectedPaymentOptionId,
  setIsUpdatingCLAutopayEnabled,
  setShowEfaModal,
  setPaymentOptionsError,
  clearPaymentOptionsError,
  resetPaymentOptionsForRetry,
} = slice.actions

export default slice.reducer
